import React, { useMemo } from "react";
import { Box, Grid, useTheme } from "@mui/material";
import { formatNumberWithComma } from "features/helpers/utils";
import { getChainList } from "features/helpers/utils";
import { useIntl } from "react-intl";
import _ from "lodash";

const SelectedWalletsInfo = ({
  wallets = [],
  isAnyWalletSelected,
  ecosystemFilter,
  selectedChainIds,
  setSelectedChainIds,
  isTicketUserInfo,
  selectedWalletInfoRef,
}) => {
  const { formatMessage } = useIntl();
  const theme = useTheme();

  const chainList = useMemo(() => {
    return getChainList(ecosystemFilter.value);
  }, [ecosystemFilter.value]);

  const { totalValue, chainValueMap } = useMemo(() => {
    const map = {}; // chainId => value
    let total = 0;
    wallets?.forEach((wallet) => {
      wallet.walletValues?.forEach(({ chainId, value }) => {
        const val = Number(value || 0);
        map[chainId] = (map[chainId] || 0) + val;
        total += val;
      });
    });
    return { totalValue: total, chainValueMap: map };
  }, [wallets]);

  const chainValueList = _.toPairs(chainValueMap);

  if (chainValueList?.length == 0 && (wallets?.length == 0 || isTicketUserInfo))
    return <Box />;

  return (
    <Grid
      selectedWalletInfoRef
      container
      wrap="nowrap"
      sx={{
        backgroundColor: "rgba(123, 97, 255, 0.2)",
        padding: "12px 16px",
        borderRadius: "4px",
        gap: "16px",
      }}
    >
      {!isTicketUserInfo && (
        <Grid
          item
          sm={6}
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: "16px",
            margin: "8px 0",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "5px",
            }}
          >
            <Box
              sx={{
                fontSize: "12px",
                fontWeight: 500,
                color: theme.customColors.purple[600],
              }}
            >
              {isAnyWalletSelected
                ? formatMessage({ id: "global.commonWord.selectedWalletCount" })
                : formatMessage({ id: "global.commonWord.walletCount" })}
            </Box>
            <Box sx={{ fontSize: "13px", fontWeight: 500 }}>
              {wallets?.length}
            </Box>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: "5px" }}>
            <Box
              sx={{
                fontSize: "12px",
                fontWeight: 500,
                color: theme.customColors.purple[600],
              }}
            >
              {formatMessage({ id: "global.commonWord.totalWalletValue" })}
            </Box>
            <Box sx={{ fontSize: "14px", fontWeight: 500 }}>
              ${formatNumberWithComma(totalValue)}
            </Box>
          </Box>
        </Grid>
      )}

      <Grid
        item
        sm={isTicketUserInfo ? 12 : 6}
        sx={{ display: "flex", gap: "8px", flexWrap: "wrap" }}
      >
        {_.map(chainValueList, ([chainId, value]) => {
          const chain = chainList.find((c) => {
            if (typeof c.value !== typeof chainId)
              return c.value === Number(chainId);

            return c.value === chainId;
          });

          if (!chain) return null;

          return (
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                backgroundColor: "rgba(255, 255, 255, 0.80)",
                borderRadius: "4px",
                padding: "4px 8px",
                flexWrap: "nowrap",
                cursor: "pointer",
                border: selectedChainIds?.includes(chain.value)
                  ? `1px solid ${theme.customColors.purple[500]}`
                  : "1px solid transparent",
                "&:last-child": {
                  marginBottom: 0,
                },
              }}
              onClick={() => {
                // Toggle chainId in selectedChainIds
                if (selectedChainIds.includes(chain.value)) {
                  setSelectedChainIds(
                    selectedChainIds.filter((id) => id !== chain.value)
                  );
                } else {
                  setSelectedChainIds([...selectedChainIds, chain.value]);
                }
              }}
              key={chainId}
            >
              <span
                className="startRow"
                style={{ flexWrap: "nowrap", gap: "4px", fontSize: "12px" }}
              >
                <img
                  src={chain.logo_url}
                  style={{ width: "16px", height: "16px" }}
                />
                <span
                  style={{
                    fontWeight: 700,
                  }}
                >
                  {chain.name}
                </span>
                ${formatNumberWithComma(value)}
              </span>
            </Box>
          );
        })}
      </Grid>
    </Grid>
  );
};

export default SelectedWalletsInfo;
